import React, { useEffect, useMemo, useState } from "react";
import VStack from "../../ui_atoms/VStack";
import { Linking, StyleSheet } from "react-native";
import { Cct, Contract, Mission } from "../../__generated__/graphql";
import { CctDetailsFieldFullValueFieldsFragment } from "../../__generated__/operations/graphql";
import { useI18n } from "../../hooks/i18n-hooks";
import { useAtomValue } from "jotai";
import { allCCTState, defaultCCTState, liftBasedNoCCTState } from "../../atoms/core";
import Callout from "../../ui_atoms/Callout";
import { Buildable } from "ts-essentials";
import { isNoCct, useContractCct, useLoadCCTs } from "../../hooks/cct-utils";
import SelectField from "../SelectField";
import MissionCctDetails from "./MissionCctDetails";
import { BodyLargeText, BodyMediumText, BodySmallText, LinkSmallText } from "../FormattedText";
import { useIntercom } from "../../hooks/intercom";
import MarkdownView from "../MarkdownView";
import { useTheme } from "../../hooks/use-theme";
import { useCurrentProfile } from "../../hooks/use-current-profile";
import OptionsSelector from "../OptionsSelector";
import InfoButton from "../InfoButton";
import Loading from "../Loading";

type MissionCCTProps = {
    mission: Buildable<Mission>;
    setCCTId: (cctId?: Cct) => void;
    setCctDetails: (value: CctDetailsFieldFullValueFieldsFragment) => void;
    setScreenIsValid: (value: boolean) => void;
};

const MissionCCT: React.FC<MissionCCTProps> = ({
    mission,
    setCCTId,
    setCctDetails,
    setScreenIsValid,
}) => {
    const cctsLoading = useLoadCCTs(
        mission.currentContract?.workingAddress?.locality?.state?.code,
        mission.legalEmployer?.useLseCct === undefined ? true : mission.legalEmployer?.useLseCct,
        mission.currentContract?.startDate,
    );
    const { colors, gridSize } = useTheme();
    const currentProfile = useCurrentProfile();
    const isPrivateClient = currentProfile?.isPrivateClient || false;
    const i18n = useI18n();
    const useLse = mission.legalEmployer?.useLseCct;
    const { presentMessageComposer, presentArticleId } = useIntercom();
    const allCCT = useAtomValue(allCCTState);
    // default CCT is the CCT Location de Service.
    const defaultCCT = useAtomValue(defaultCCTState);
    // this is the CCT representing no cct in no LSE
    const liftBasedNoCct = useAtomValue(liftBasedNoCCTState);
    const cct_choices = useMemo(() => {
        if (allCCT && !cctsLoading) {
            const filteredCCT = allCCT
                .filter((cct) => {
                    // we filter the no CCT (that represent in fact no CCT)
                    // and if we are in LSE we also filter the location de service that is the default
                    return (
                        cct.active &&
                        (!useLse || cct.cctEditionId !== defaultCCT?.cctEditionId) &&
                        !isNoCct(cct)
                    );
                })
                .map((cct) => {
                    return {
                        value: cct.cctContractId,
                        label: cct.name,
                    };
                });
            return filteredCCT;
        }
        return null;
    }, [allCCT, useLse, defaultCCT, cctsLoading]);

    const missionCct = useContractCct(mission.currentContract as Contract);

    const cctLink = useMemo(() => {
        if (missionCct && missionCct.active && !isNoCct(missionCct)) {
            let onPressMissionlink = () => {
                void Linking.openURL(
                    missionCct.cctUrl ||
                        `https://www.tempservice.ch/fr/tempdata/detail.php?cid=${missionCct.cctContractId}&vid=${missionCct.cctVersionId}&eid=${missionCct.cctEditionId}`,
                );
            };
            if (missionCct && missionCct.intercomMainArticleId) {
                onPressMissionlink = () => {
                    void presentArticleId(missionCct.intercomMainArticleId as string);
                };
            }
            return (
                <LinkSmallText onPress={onPressMissionlink}>
                    {i18n.t("screens.user.create_mission_cct.cct_details_link")}
                </LinkSmallText>
            );
        }
        return null;
    }, [missionCct, i18n, presentArticleId]);

    const [hasCCTInitialized, setHasCCTInitialized] = useState(false);

    const hasCct = useMemo(() => {
        if (isPrivateClient) {
            // for private client we do not allow to choose the CCT.
            // CCT has been set automatically when selecting the activity
            return true;
        } else if (!mission.currentContract?.cctContractId && !hasCCTInitialized) {
            // when not initialized we need to choose between yes or no
            return undefined;
        } else if (!mission.currentContract?.cctContractId) {
            // if set to null it means we choose yes but we still need to choose the cct
            return true;
        } else if (useLse) {
            // when we choose no in lse we in fact have selected the default CCT
            return mission.currentContract?.cctContractId !== defaultCCT?.cctContractId;
        } else {
            // when we choose no in nolse we in fact have selected the no cct
            return mission.currentContract?.cctContractId !== liftBasedNoCct?.cctContractId;
        }
    }, [
        useLse,
        hasCCTInitialized,
        defaultCCT,
        liftBasedNoCct,
        isPrivateClient,
        mission.currentContract?.cctContractId,
    ]);

    useEffect(() => {
        // this effect will update to the last available CCT edition if the mission is linked
        // to a cct that has a new edition. It typically happen when we duplicate an existing old mission
        if (allCCT) {
            const lastCctEditionOfMission = allCCT.find(
                (cct) => cct.cctContractId === mission.currentContract?.cctContractId,
            );
            if (
                lastCctEditionOfMission &&
                mission.currentContract?.cctEditionId !== lastCctEditionOfMission.cctEditionId
            ) {
                // In this case, it means that the cctEditionId stored in the mission has changed from the cctEditionId returned by the server.
                // In this case, we reset the Cct to the new one (from the server), and remove all previously stored questions/answers pairs.
                setCCTId(lastCctEditionOfMission);
                setCctDetails({ readableValues: [], tempdataCallValues: [] });
            }
        }
    }, [
        allCCT,
        mission.currentContract?.cctContractId,
        mission.currentContract?.cctEditionId,
        setCCTId,
        setCctDetails,
    ]);

    return cctsLoading ? (
        <Loading />
    ) : (
        <VStack gap={1} alignment="stretch">
            {!isPrivateClient ? (
                <OptionsSelector<boolean | undefined>
                    shrinked
                    value={hasCct}
                    onSelect={(newValue) => {
                        setHasCCTInitialized(true);
                        if (newValue) {
                            // if we select that there is a CCT, we set the CCT to null so user will need to choose it
                            setCCTId(undefined);
                        } else {
                            // If the answer is "No", we either select the default LSE (if legal employer use it) or the No-CCT-LSE
                            if (useLse && defaultCCT) {
                                setCCTId(defaultCCT);
                            } else if (!useLse && liftBasedNoCct) {
                                setCCTId(liftBasedNoCct);
                            } else {
                                throw new Error(
                                    "No default CCT found for this mission, while trying to set No-CCT answer",
                                );
                            }
                        }
                    }}
                    data={[
                        { label: i18n.t("yes"), value: true },
                        { label: i18n.t("no"), value: false },
                    ]}
                />
            ) : (
                <BodyMediumText color={colors.greyAccessible}>
                    {i18n.t("screens.user.create_mission_cct.private_description_top")}
                </BodyMediumText>
            )}
            {!isPrivateClient && hasCct !== undefined && (
                <VStack gap="2/3">
                    {useLse && missionCct?.cctEditionId === defaultCCT?.cctEditionId ? (
                        <BodyMediumText color={colors.greyAccessible}>
                            {i18n.t("screens.user.create_mission_cct.default_lse")}{" "}
                            <InfoButton
                                onPress={() => {
                                    void presentArticleId("10063860"); // article about why the LSE is selected by default
                                }}
                            />
                        </BodyMediumText>
                    ) : !useLse && missionCct?.cctEditionId === liftBasedNoCct?.cctEditionId ? (
                        <MarkdownView
                            markdown={i18n.t("screens.user.create_mission_cct.no-cct-info")}
                        />
                    ) : (
                        <VStack gap="1/2">
                            <BodyLargeText color={colors.primary}>
                                {i18n.t("screens.user.create_mission_cct.select_cct")}
                            </BodyLargeText>
                            <>{/* Add extra space */}</>
                            <SelectField
                                testID="cct-select"
                                style={styles.items}
                                value={missionCct?.active ? missionCct.cctContractId : undefined}
                                data={cct_choices}
                                isDynamicHeight
                                onSelect={(selectedItem) => {
                                    const selectedCct = allCCT?.find(
                                        (c) => c.cctContractId === selectedItem.value,
                                    );
                                    setCCTId(selectedCct);
                                }}
                                bottomComponent={
                                    <MarkdownView
                                        markdown={i18n.t(
                                            "screens.user.create_mission_cct.no_cct_select",
                                        )}
                                        markdownStyle={{
                                            paragraph: {
                                                color: colors.greyAccessible,
                                                // ...fonts.bodySmall,
                                            },
                                        }}
                                        onLinkPress={() => {
                                            void presentMessageComposer();
                                            return false;
                                        }}
                                    />
                                }
                            />
                            {missionCct && !missionCct.active && (
                                <Callout
                                    mode="warning"
                                    style={{
                                        paddingTop: gridSize / 2,
                                        paddingBottom: gridSize / 2,
                                    }}
                                    markdown={i18n.t(
                                        "screens.user.create_mission_cct.unsupported_cct",
                                        {
                                            cctName: missionCct.name,
                                        },
                                    )}
                                    onLinkPress={() => {
                                        void presentMessageComposer();
                                        return false;
                                    }}
                                />
                            )}
                            {useLse &&
                                missionCct &&
                                missionCct.cctEditionId === defaultCCT?.cctEditionId && (
                                    <BodySmallText color={colors.greyAccessible}>
                                        {i18n.t(
                                            "screens.user.create_mission_cct.default_cct_description",
                                        )}
                                    </BodySmallText>
                                )}
                            {cctLink}
                        </VStack>
                    )}
                </VStack>
            )}
            {missionCct && missionCct.active && hasCct !== undefined && (
                <>
                    <MissionCctDetails
                        mission={mission}
                        missionCct={missionCct}
                        setCctDetails={setCctDetails}
                        setScreenIsValid={setScreenIsValid}
                    />
                </>
            )}
        </VStack>
    );
};

const styles = StyleSheet.create({
    items: {
        textAlign: "auto",
        alignSelf: "stretch",
    },
});

export default MissionCCT;
